import { useState, useEffect, useCallback } from 'react'
import { fetchCommunications, createCommunication, deleteCommunication } from '../lib/supabase'

const CHANNELS = ['call', 'email', 'dm', 'meeting', 'other']
const CHANNEL_ICON = { call: '📞', email: '✉️', dm: '💬', meeting: '🤝', other: '•' }

function when(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const days = Math.floor((Date.now() - d.getTime()) / 86400000)
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days}d ago`
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })
}

const lbl = { fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 3, display: 'block' }
const inp = {
  width: '100%', padding: '6px 8px', fontSize: 12, borderRadius: 6,
  border: '1px solid var(--border)', background: 'var(--bg3)',
  color: 'var(--text)', fontFamily: 'var(--font-body)', outline: 'none',
}

/**
 * Conversation history for a lead, plus a compact form to log a new one.
 * Logging moves the lead's last_contacted / next_follow_up through a database
 * trigger, so `onLogged` refreshes the parent list to pick those up.
 */
export default function LeadTimeline({ lead, currentUser, onLogged, isMobile }) {
  const [comms, setComms] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [form, setForm] = useState({
    channel: 'call', direction: 'outbound', summary: '',
    occurred_on: new Date().toISOString().slice(0, 10), next_follow_up: '',
  })

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await fetchCommunications(lead.id)
    setComms(data || [])
    setLoading(false)
  }, [lead.id])

  useEffect(() => { load() }, [load])

  const save = async () => {
    if (!form.summary.trim()) { setError('Write what happened.'); return }
    setSaving(true); setError(null)
    // Midday so a timezone shift can't drag the entry to the previous day.
    const { error: err } = await createCommunication({
      org_id: currentUser.org_id,
      user_id: currentUser.id,
      user_name: currentUser.agent_name || currentUser.full_name || 'User',
      lead_id: lead.id,
      brand: lead.brand || null,
      contact_name: lead.contact_name || null,
      channel: form.channel,
      direction: form.direction,
      summary: form.summary.trim(),
      occurred_at: new Date(`${form.occurred_on}T12:00:00`).toISOString(),
      next_follow_up: form.next_follow_up || null,
    })
    setSaving(false)
    if (err) { setError(err.message); return }
    setForm(f => ({ ...f, summary: '', next_follow_up: '' }))
    setAdding(false)
    await load()
    if (onLogged) onLogged()
  }

  const remove = async (id) => {
    if (!window.confirm('Delete this entry?')) return
    await deleteCommunication(id)
    load()
  }

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', fontWeight: 600 }}>
          Timeline{comms.length > 0 && ` · ${comms.length}`}
        </div>
        <button onClick={() => { setAdding(a => !a); setError(null) }} style={{
          padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(139,92,246,0.3)',
          background: 'rgba(139,92,246,0.1)', color: '#8b5cf6', fontSize: 11, fontWeight: 600,
          cursor: 'pointer', fontFamily: 'var(--font-body)',
        }}>{adding ? 'Cancel' : '+ Log a touchpoint'}</button>
      </div>

      {adding && (
        <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: 10, marginBottom: 10, border: '1px solid var(--border)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={lbl}>Channel</label>
              <select value={form.channel} onChange={e => setForm(f => ({ ...f, channel: e.target.value }))} style={{ ...inp, cursor: 'pointer' }}>
                {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Direction</label>
              <select value={form.direction} onChange={e => setForm(f => ({ ...f, direction: e.target.value }))} style={{ ...inp, cursor: 'pointer' }}>
                <option value="outbound">outbound</option>
                <option value="inbound">inbound</option>
              </select>
            </div>
            <div>
              <label style={lbl}>When</label>
              <input type="date" value={form.occurred_on} onChange={e => setForm(f => ({ ...f, occurred_on: e.target.value }))} style={inp} />
            </div>
            <div>
              <label style={lbl}>Next follow up</label>
              <input type="date" value={form.next_follow_up} onChange={e => setForm(f => ({ ...f, next_follow_up: e.target.value }))} style={inp} />
            </div>
          </div>
          <textarea
            value={form.summary}
            onChange={e => setForm(f => ({ ...f, summary: e.target.value }))}
            placeholder="Asked for Q4 rates on a 3-post package. Sending a proposal Thursday."
            rows={2}
            style={{ ...inp, resize: 'vertical', marginBottom: 8 }}
          />
          {error && <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 8 }}>{error}</div>}
          <button onClick={save} disabled={saving} style={{
            padding: '6px 14px', borderRadius: 7, border: 'none',
            background: saving ? 'var(--bg2)' : 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
            color: '#fff', fontSize: 11, fontWeight: 700,
            cursor: saving ? 'wait' : 'pointer', fontFamily: 'var(--font-body)',
          }}>{saving ? 'Saving…' : 'Log it'}</button>
          {form.next_follow_up && (
            <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 10 }}>
              Moves this lead's follow-up to {form.next_follow_up}
            </span>
          )}
        </div>
      )}

      {loading && <div style={{ fontSize: 11, color: 'var(--text3)' }}>Loading…</div>}

      {!loading && comms.length === 0 && !adding && (
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>
          Nothing logged yet. Calls and emails you log here move the follow-up date automatically.
        </div>
      )}

      {!loading && comms.map(c => (
        <div key={c.id} style={{
          display: 'flex', gap: 10, padding: '8px 0',
          borderBottom: '1px solid var(--border)', alignItems: 'flex-start',
        }}>
          <span style={{ fontSize: 14, lineHeight: 1.3 }} title={c.channel}>{CHANNEL_ICON[c.channel] || '•'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{c.summary}</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span>{c.direction === 'inbound' ? '← inbound' : '→ outbound'}</span>
              <span>{c.user_name}</span>
              <span>{when(c.occurred_at)}</span>
              {c.next_follow_up && <span style={{ color: '#8b5cf6' }}>follow up {c.next_follow_up}</span>}
            </div>
          </div>
          <button onClick={() => remove(c.id)} title="Delete" style={{
            background: 'none', border: 'none', color: 'var(--text3)',
            cursor: 'pointer', fontSize: 14, padding: '0 2px', lineHeight: 1,
          }}>×</button>
        </div>
      ))}
    </div>
  )
}
